import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, Edit2, Trash2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const empty = { name: "", short_description: "", image_path: "", sort_order: 0, active: true };

export default function Collections() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(empty);
  const [delId, setDelId] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/collections");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEdit(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => {
    setEdit(c);
    setForm({ name: c.name, short_description: c.short_description || "", image_path: c.image_path || "", sort_order: c.sort_order || 0, active: c.active });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name) { toast.error("Ad zorunlu"); return; }
    if (edit) await api.put(`/admin/collections/${edit.id}`, form);
    else await api.post("/admin/collections", form);
    toast.success("Kaydedildi");
    setOpen(false);
    load();
  };

  const remove = async () => {
    await api.delete(`/admin/collections/${delId}`);
    toast.success("Silindi");
    setDelId(null);
    load();
  };

  return (
    <div className="space-y-6" data-testid="admin-collections-page">
      <div className="flex items-end justify-between">
        <div>
          <span className="label-tag">Koleksiyonlar</span>
          <h1 className="font-serif text-4xl mt-2">Koleksiyon Yönetimi</h1>
        </div>
        <Button onClick={openCreate} className="btn-luxe text-xs" data-testid="admin-coll-new-btn"><Plus size={14}/> Yeni Koleksiyon</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((c) => (
          <div key={c.id} className="bg-white border border-[#eae5dc] overflow-hidden" data-testid={`coll-card-${c.id}`}>
            {c.image_path && <img src={c.image_path} alt="" className="w-full aspect-video object-cover" />}
            <div className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-serif text-xl">{c.name}</h3>
                  <p className="text-sm text-[#6b655c] mt-1">{c.short_description}</p>
                </div>
                <span className={`text-[0.65rem] uppercase tracking-wider px-2 py-1 ${c.active ? "bg-emerald-50 text-emerald-800" : "bg-stone-100 text-stone-700"}`}>
                  {c.active ? "Aktif" : "Pasif"}
                </span>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button onClick={() => openEdit(c)} className="p-2 hover:bg-[#f7f5f0]" data-testid={`coll-edit-${c.id}`}><Edit2 size={14}/></button>
                <button onClick={() => setDelId(c.id)} className="p-2 hover:bg-red-50 text-red-600"><Trash2 size={14}/></button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-[#6b655c] col-span-full">Koleksiyon yok.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{edit ? "Koleksiyonu Düzenle" : "Yeni Koleksiyon"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Field label="Ad *">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="coll-form-name" />
            </Field>
            <Field label="Açıklama">
              <Textarea rows={3} value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} />
            </Field>
            <ImageUpload
              label="Görsel"
              value={form.image_path}
              onChange={(url) => setForm({ ...form, image_path: url })}
              testid="coll-form-image"
            />
            <div className="grid grid-cols-2 gap-4">
              <Field label="Sıra">
                <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} />
              </Field>
              <div className="flex items-center justify-between">
                <span className="text-sm">Aktif</span>
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button>
            <Button onClick={save} className="btn-luxe text-xs" data-testid="coll-form-save-btn">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>Bu işlem geri alınamaz.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={remove}>Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs uppercase tracking-wider text-[#6b655c] block mb-1.5">{label}</label>
    {children}
  </div>
);
